import React from 'react';
import { object, bool, arrayOf, func, string } from 'prop-types';
import { reduce } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Translation } from 'react-i18next';
import { toActiveLocale } from '../../../../services/Translation';
import config from '../../../../conf';
import i18n from 'i18next';
import { FormHelperText } from '@material-ui/core';
import ErrorMsg from '../../../scenes/components/ErrorMsg';
import DocumentsRow from './DocumentsRow';

class EitherOrPickerDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: null,
    };
    this.handleEntering = this.handleEntering.bind(this);
  }

  handleEntering = () => {
    this.radioGroupRef.focus();
  };

  handleCancel = () => {
    this.props.onClose(this.props.value);
  };

  handleOk = () => {
    this.props.onClose(this.state.value);
    this.setState({
      value: null,
    });
  };

  handleChange = (event, value) => {
    this.setState({ value });
  };

  render() {
    const { options = {}, t, lang, ...other } = this.props;
    const { either, or } = options;
    return (
      <Dialog
        onClose={this.handleCancel}
        disableEscapeKeyDown
        maxWidth="xs"
        TransitionProps={{ onEntering: this.handleEntering }}
        aria-labelledby="confirmation-dialog-title"
        {...other}
      >
        <DialogTitle id="confirmation-dialog-title">
          {t('eitherOrDocSelectionPrompt')}
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            ref={ref => {
              this.radioGroupRef = ref;
            }}
            aria-label="Ringtone"
            name="ringtone"
            value={this.state.value}
            onChange={this.handleChange}
          >
            {either && (
              <FormControlLabel
                value={either.uuid}
                key={either.uuid}
                control={<Radio />}
                label={toActiveLocale(either, lang)}
              />
            )}
            {or && (
              <FormControlLabel
                value={or.uuid}
                key={or.uuid}
                control={<Radio />}
                label={toActiveLocale(or, lang)}
              />
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={this.handleOk}
            color="primary"
            disabled={!this.state.value}
          >
            {t('ok')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

EitherOrPickerDialog.propTypes = {
  onClose: func,
  value: string,
};

const styles = theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    width: '100%',
    marginTop: theme.spacing(3),
    overflowX: 'auto',
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 700,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
  },
  icon: {
    margin: theme.spacing(0.5),
  },
  barLayout: {
    padding: theme.spacing(1),
  },
  paper: {
    width: '80%',
    maxHeight: 435,
  },
  file: {
    display: 'none',
  },
  fileLabel: {
    fontSize: '0.6rem',
    color: theme.palette.text.secondary,
  },
  uploadButton: {
    display: 'block',
  },
});

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class SupportingDocumentsComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      file: {},
      showPickerDialog: false,
      eitherOrOptions: {},
      idError: null,
      errorText: '',
    };
    this.eitherOrMap = this.mapEitherOrDocs(props.eitherOrDocuments);
  }

  componentWillUnmount() {
    this.eitherOrMap = null;
  }

  mapEitherOrDocs = docs =>
    reduce(
      docs || [],
      (map, doc) => {
        map[this.createEitherOrId(doc.either, doc.or)] = doc;
        return map;
      },
      {}
    );

  createEitherOrId = (either, or) => `__EITHER__${either.uuid}__OR__${or.uuid}`;

  handleFileUpload = (e, id, code) => {
    const {
      uploadFailure,
      documents,
      pictureFileTypes,
      fileTypes,
      deleteDocumentRequest,
    } = this.props;
    const files = e.target.files;
    if (!files || !files.length) return;
    const imageTypes = pictureFileTypes.map(t => `image/${t.toLowerCase()}`);
    const changingDocument = documents.find(d => d.documenttypeCode === code);

    if (code === '481' && !imageTypes.includes(files[0].type)) {
      this.setState(prevState => {
        return {
          ...prevState,
          idError: id,
          errorText: 'unsupportedImageFileType',
        };
      });
      return uploadFailure(
        i18n.t('unsupportedImageFileType', { pictureFileTypes })
      );
    }

    if (!config.supportedDocTypes.includes(files[0].type)) {
      this.setState(prevState => {
        return { ...prevState, idError: id, errorText: 'unsupportedDocType' };
      });
      return uploadFailure(i18n.t('unsupportedDocType', { fileTypes }));
    }

    if (files[0].size > 1024 ** 2) {
      this.setState(prevState => {
        return { ...prevState, idError: id, errorText: 'maxFileSizeExceeded' };
      });

      if (changingDocument) {
        return deleteDocumentRequest(id, changingDocument.uuid);
      }
      return;
    }

    this.props.upload(files[0], id, (changingDocument || {}).uuid);

    this.setState(prevState => {
      return { ...prevState, idError: false, errorText: false };
    });
  };

  onPickerDialogClose = id => {
    this.props.upload(this.state.file, id);
    this.setState({
      file: {},
      eitherOrOptions: {},
      showPickerDialog: false,
    });
  };

  handleEitherOrUpload = (e, id) => {
    const files = e.target.files;
    this.setState({
      file: files[0],
      eitherOrOptions: this.eitherOrMap[id],
      showPickerDialog: true,
    });
  };

  getTranslatedError(translationKey) {
    const { pictureFileTypes, fileTypes } = this.props;

    if (!i18n.exists(translationKey || '')) {
      return '';
    }
    if (translationKey === 'unsupportedImageFileType') {
      return i18n.t('unsupportedImageFileType', { pictureFileTypes });
    }
    if (translationKey === 'unsupportedDocType') {
      return i18n.t('unsupportedDocType', { fileTypes });
    }

    return i18n.t(this.state.errorText);
  }

  renderEitherOrRow = (docSpec, classes, t) => {
    const { either, or } = docSpec;
    const { documentMap, readOnly, lang } = this.props;
    const doc = documentMap[either.uuid] || documentMap[or.uuid] || {};
    return (
      <TableRow className={classes.row} key={either.uuid}>
        <CustomTableCell component="th" scope="row">
          {`${toActiveLocale(either, lang)}  ${t('or')}  ${toActiveLocale(
            or,
            lang
          )} *`}
        </CustomTableCell>
        <CustomTableCell>{doc ? doc.filename : ''}</CustomTableCell>
        {!readOnly && (
          <CustomTableCell>
            <input
              type="file"
              name="file"
              onChange={e =>
                this.handleEitherOrUpload(e, this.createEitherOrId(either, or))
              }
            />
          </CustomTableCell>
        )}
      </TableRow>
    );
  };

  render() {
    const {
      classes,
      readOnly = false,
      requiredDocuments,
      optionalDocuments,
      eitherOrDocuments,
      lang,
      error,
    } = this.props;
    return (
      <Translation>
        {t => {
          return (
            <div>
              {error && <ErrorMsg error={error} />}
              <EitherOrPickerDialog
                classes={{
                  paper: classes.paper,
                }}
                open={this.state.showPickerDialog}
                onClose={this.onPickerDialogClose}
                t={t}
                options={this.state.eitherOrOptions}
                lang={lang}
              />
              <Typography
                className={classes.barLayout}
                style={{ color: '#ffffff', background: '#4CAF50' }}
                variant="subtitle1"
                align="left"
                gutterBottom
              >
                {t('supportingDocs')}
              </Typography>
              <Typography variant="subtitle2" align="center" gutterBottom>
                {t('supportingDocumentsCaption')}
              </Typography>
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                direction="row"
              >
                <Grid item xs={8}>
                  <Paper className={classes.root} elevation={3}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <CustomTableCell>{t('docType')}</CustomTableCell>
                          <CustomTableCell>{t('filename')}</CustomTableCell>
                          {!readOnly && (
                            <CustomTableCell>{t('uploadMax')}</CustomTableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requiredDocuments.map(docSpec => (
                          <DocumentsRow
                            supportingDocumentsProps={this.props}
                            supportingDocumentsState={this.state}
                            docSpec={docSpec}
                            classes={classes}
                            t={t}
                            type={0}
                            handleFileUpload={this.handleFileUpload}
                          />
                        ))}
                        {optionalDocuments.map(docSpec => (
                          <DocumentsRow
                            supportingDocumentsProps={this.props}
                            supportingDocumentsState={this.state}
                            docSpec={docSpec}
                            classes={classes}
                            t={t}
                            type={1}
                            handleFileUpload={this.handleFileUpload}
                          />
                        ))}
                        {eitherOrDocuments.map(docSpec =>
                          this.renderEitherOrRow(docSpec, classes, t)
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </div>
          );
        }}
      </Translation>
    );
  }
}

SupportingDocumentsComponent.propTypes = {
  classes: object.isRequired,
  readOnly: bool,
  requiredDocuments: arrayOf(object).isRequired,
  optionalDocuments: arrayOf(object).isRequired,
  eitherOrDocuments: arrayOf(object).isRequired,
  documents: arrayOf(object).isRequired,
  documentMap: object.isRequired,
  error: string,
};

export default withStyles(styles)(SupportingDocumentsComponent);



// WEBPACK FOOTER //
// ./src/view/forms/Application/SupportingDocuments/SupportingDocumentsComponent.js